import React, { useState, useEffect } from 'react';
import { Edit2, Save, X, RotateCcw, AlertCircle, AlertTriangle } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const RoleHierarchyManager = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRole, setEditingRole] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await api.get('/roles');
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Failed to load role configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (role) => {
    setEditingRole(role.id);
    setEditForm({ name: role.name, description: role.description });
  };

  const handleCancelEdit = () => {
    setEditingRole(null);
    setEditForm({ name: '', description: '' });
  };

  const handleSaveEdit = async (roleId) => {
    if (!editForm.name.trim()) {
      toast.error('Role name cannot be empty');
      return;
    }

    try {
      setSaving(true);
      const response = await api.patch(`/roles/${roleId}`, editForm);
      
      if (response.data.success) {
        toast.success(response.data.message);
        await fetchRoles();
        setEditingRole(null);
      }
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error(error.response?.data?.detail || 'Failed to update role');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefaults = async () => {
    try {
      setSaving(true);
      const response = await api.post('/roles/reset-defaults');
      
      if (response.data.success) {
        toast.success(response.data.message);
        await fetchRoles();
      }
    } catch (error) {
      console.error('Error resetting roles:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset roles');
    } finally {
      setSaving(false);
      setShowResetConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Role Hierarchy Management</h2>
          <p className="text-sm text-gray-500 mt-1">
            Customize role display names and descriptions. Internal IDs remain unchanged.
          </p>
        </div>
        {showResetConfirm ? (
          <div className="flex items-center gap-2 bg-red-50 border border-red-300 rounded-lg px-3 py-2">
            <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
            <span className="text-xs text-red-800 font-medium">Reset all to defaults?</span>
            <button
              onClick={handleResetToDefaults}
              disabled={saving}
              className="px-2 py-1 text-white bg-red-600 hover:bg-red-700 rounded text-xs font-bold disabled:opacity-50"
            >
              {saving ? 'Resetting...' : 'Yes, Reset'}
            </button>
            <button
              onClick={() => setShowResetConfirm(false)}
              className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded text-xs"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowResetConfirm(true)}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            <RotateCcw className="w-4 h-4" />
            Reset to Defaults
          </button>
        )}
      </div>

      {/* Info Banner */}
      <div className="px-6 py-3 bg-blue-50 border-b border-blue-100 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          <p className="font-medium">Role names are display-only changes</p>
          <p className="mt-1">
            Editing role names only changes how they appear in the UI. Internal permissions, 
            hierarchy levels, and system functionality remain unchanged.
          </p>
        </div>
      </div>

      {/* Roles Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Level
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Display Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {roles.map((role) => (
              <tr key={role.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-800 text-sm font-semibold">
                    {role.hierarchy_level}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <code className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm font-mono">
                    {role.id}
                  </code>
                </td>
                <td className="px-6 py-4">
                  {editingRole === role.id ? (
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Role display name"
                    />
                  ) : (
                    <span className="text-sm font-medium text-gray-900">{role.name}</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  {editingRole === role.id ? (
                    <textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Role description"
                      rows={2}
                    />
                  ) : (
                    <span className="text-sm text-gray-600">{role.description}</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingRole === role.id ? (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleSaveEdit(role.id)}
                        disabled={saving}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Save changes"
                      >
                        <Save className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        disabled={saving}
                        className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Cancel"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleEditClick(role)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit role"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer Note */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          <strong>Note:</strong> Role hierarchy levels determine approval flow order. 
          Lower numbers have higher authority (1 = highest). Hierarchy levels cannot be changed.
        </p>
      </div>
    </div>
  );
};

export default RoleHierarchyManager;
